// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { map } from "../../../../../../io/hyperify/core/functions/map";
import {
    isEmailOption,
} from "../../../../../../io/hyperify/core/store/types/product/features/EmailOption";
import { ProductFeatureId } from "../../../../../../io/hyperify/core/store/types/product/features/ProductFeatureId";
import { ProductFeatureCategory } from "../../../../../../io/hyperify/core/store/types/product/features/ProductFeatureCategory";
import { isProductFeature } from "../../../../../../io/hyperify/core/store/types/product/features/ProductFeature";
import { isArray } from "../../../../../../io/hyperify/core/types/Array";
import { isBoolean } from "../../../../../../io/hyperify/core/types/Boolean";
import { SiTranslationUtils } from "../../../../../../io/hyperify/core/SiTranslationUtils";
import { SiUtils } from "../../../../../../io/hyperify/core/SiUtils";
import { SiType } from "../../../../../../io/hyperify/core/types/SiType";
import { PRODUCT_FEATURE_CLASS_NAME } from "../../../../core/constants/className";
import {
    getBackupFeatureTranslation,
    getCPUShareTranslation,
    getDiskRaidTypeFeatureTranslation,
    getDiskTypeFeatureTranslation,
    getDiskUsageFeatureTranslation,
    getEmailOptionTranslation,
    getNetworkIpTypeFeatureTranslation,
    getNetworkTypeFeatureTranslation,
    getNetworkZoneFeatureTranslation,
    getOperatingSystemFeatureTranslation,
    getPHPSoftwareTranslation,
    getVirtualizationTypeFeatureTranslation,
    getWebServerTranslation,
    T_COMMON_BYTE_SHORT,
    T_COMMON_TRAFFIC_MONTHLY,
    T_COMMON_VALUE_PIECES,
    T_COMMON_VALUE_PIECES_PER_DAY,
    T_COMMON_VALUE_PIECES_PER_HOUR,
    T_COMMON_VALUE_PIECES_PER_MINUTE,
    T_COMMON_VALUE_PIECES_PER_SECOND,
} from "../../../../../../io/hyperify/core/store/constants/storeTranslation";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import { T_COMMON_OR } from "../../../../core/constants/translation";

export interface ProductFeatureDataProps {
    readonly t: TranslationFunction;
    readonly className?: string;
    readonly id?: ProductFeatureId;
    readonly category?: ProductFeatureCategory;
    readonly value?: any;
    readonly dark ?: boolean;
    readonly translationParams ?: { readonly [key2: string]: any };
}

export function ProductFeatureData (props: ProductFeatureDataProps) {

    const t = props?.t;
    const value = props?.value;
    const translationParams = props?.translationParams;
    const className = props?.className;
    const id: ProductFeatureId | undefined = props?.id;
    const dark = props?.dark ?? false;

    const classNames = (
        PRODUCT_FEATURE_CLASS_NAME
        + (className ? ' ' + className : '')
        + (dark ? ' ' + PRODUCT_FEATURE_CLASS_NAME + '-dark' : '')
    );

    if( isArray(value) ) {
        const sep = ` ${t(T_COMMON_OR, translationParams)} `;
        return (
            <>{map(
                value,
                (item, index) => {
                    const itemProps : ProductFeatureDataProps = {
                        ...props,
                        value: item
                    };
                    return (
                        <>
                            {index !== 0 ? sep : null}
                            <ProductFeatureData key={`${id}:${index}`} {...itemProps} />
                        </>
                    );
                }
            )}</>
        );
    }

    if ( isProductFeature(value) ) {
        return (
            <ProductFeatureData
                t={t}
                id={value?.id}
                category={value?.category}
                value={value?.value}
                dark={dark}
                translationParams={translationParams}
            />
        );
    }

    if ( isBoolean(value) ) {
        return value === true ? <span className={classNames}> &#10004; </span> : <span className={classNames}> &#x2717; </span>;
    }

    if ( id ) {
        switch (id) {

            case ProductFeatureId.DISK_TYPE        :
            case ProductFeatureId.DISK_2_TYPE        :
            case ProductFeatureId.DISK_3_TYPE        :
                return <span className={classNames}>{t(getDiskTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.DISK_SIZE        :
            case ProductFeatureId.DISK_2_SIZE        :
            case ProductFeatureId.DISK_3_SIZE        :
                return <span className={classNames}>{SiTranslationUtils.formatSiBinary(
                    SiUtils.getBytes(value, SiType.GIGA), T_COMMON_BYTE_SHORT, t)}</span>;

            case ProductFeatureId.DISK_RAID        :
            case ProductFeatureId.DISK_2_RAID        :
            case ProductFeatureId.DISK_3_RAID        :
                return <span className={classNames}>{t(getDiskRaidTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.DISK_BACKUP        :
            case ProductFeatureId.DISK_2_BACKUP        :
            case ProductFeatureId.DISK_3_BACKUP        :
                return <span className={classNames}>{t(getBackupFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.MEMORY_SIZE      :
                return <span className={classNames}>{SiTranslationUtils.formatSiBinary(
                    SiUtils.getBytes(value, SiType.MEGA), T_COMMON_BYTE_SHORT, t)}</span>;

            case ProductFeatureId.PHP_MEMORY      :
                return <span className={classNames}>{SiTranslationUtils.formatSiBinary(
                    SiUtils.getBytes(value, SiType.MEGA), T_COMMON_BYTE_SHORT, t)}</span>;

            case ProductFeatureId.NETWORK_TYPE     :
                return <span className={classNames}>{t(getNetworkTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.NETWORK_ZONE     :
                return <span className={classNames}>{t(getNetworkZoneFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.NETWORK_IP4     :
                return <span className={classNames}>{t(getNetworkIpTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.NETWORK_IP6     :
                return <span className={classNames}>{t(getNetworkIpTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.NETWORK_NET6     :
                return <span className={classNames}>{t(getNetworkIpTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.VPS_TYPE     :
                return <span className={classNames}>{t(getVirtualizationTypeFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.VPS_OS     :
                return <span className={classNames}>{t(getOperatingSystemFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.DISK_USAGE     :
                return <span className={classNames}>{t(getDiskUsageFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.DISK_2_USAGE     :
                return <span className={classNames}>{t(getDiskUsageFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.DISK_3_USAGE     :
                return <span className={classNames}>{t(getDiskUsageFeatureTranslation(value), translationParams)}</span>;

            case ProductFeatureId.WEBSERVER_SOFTWARE:
                return <span className={classNames}>{t(getWebServerTranslation(value), translationParams)}</span>;

            case ProductFeatureId.PHP_SOFTWARE:
                return <span className={classNames}>{t(getPHPSoftwareTranslation(value), translationParams)}</span>;

            case ProductFeatureId.NETWORK_TRAFFIC:
                return <span className={classNames}>{SiTranslationUtils.formatSiBinary(
                    SiUtils.getBytes(value, SiType.GIGA), T_COMMON_TRAFFIC_MONTHLY, t)}</span>;

            case ProductFeatureId.CPU_SHARE:
                return <span className={classNames}>{t( getCPUShareTranslation(value), translationParams )}</span>;

            case ProductFeatureId.CPU_AMOUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.MAILBOX_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.ADMIN_USER_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DOMAIN_QUOTA :
            case ProductFeatureId.MAILBOX_QUOTA :
                if (isEmailOption(value)) {
                    return <span className={classNames}>{t(getEmailOptionTranslation(value), translationParams)}</span>;
                }
                return <span className={classNames}>{SiTranslationUtils.formatSiBinary(
                    SiUtils.getBytes(value, SiType.GIGA), T_COMMON_BYTE_SHORT, t)}</span>;

            case ProductFeatureId.MAILBOX_ALIAS_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DOMAIN_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DOMAIN_ALIAS_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.SUB_DOMAIN_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.SSH_ACCOUNT_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.SFTP_ACCOUNT_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DATABASE_COUNT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_DAY, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT:
                if (isEmailOption(value)) {
                    return <span className={classNames}>{t(getEmailOptionTranslation(value), translationParams)}</span>;
                }
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_DAY, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.USER_THROTTLE_RATE:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_MINUTE, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DDOS_THROTTLE_RATE:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_SECOND, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.DDOS_LESSER_THROTTLE_RATE:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_MINUTE, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.LOGIN_THROTTLE_RATE:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_MINUTE, { ...translationParams, VALUE: value })}</span>;

            case ProductFeatureId.PASSWORD_RECOVERY_THROTTLE_RATE:
            case ProductFeatureId.PASSWORD_RECOVERY_TOTP_THROTTLE_RATE:
            case ProductFeatureId.PASSWORD_RECOVERY_APPLY_THROTTLE_RATE:
                return <span className={classNames}>{t(T_COMMON_VALUE_PIECES_PER_HOUR, { ...translationParams, VALUE: value })}</span>;


        }
    }

    return <span className={classNames}>{value ? t(value, translationParams) : ""}</span>;
}
